import { cn } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { ContactsIcon, InboxIcon, PropertiesIcon, SettingsIcon, TableIcon } from '@/components/layout/icons';
import { Kbd } from './kbd';
import type { PaletteItem } from './palette-item';

const ENTRY_ICON: Record<string, typeof ContactsIcon> = {
  contacts: ContactsIcon,
  properties: PropertiesIcon,
  deals: TableIcon,
  leads: InboxIcon,
  settings: SettingsIcon,
};

type Props = {
  readonly listboxId: string;
  readonly items: ReadonlyArray<PaletteItem>;
  readonly activeIndex: number;
  readonly onHighlight: (index: number) => void;
  readonly onSelect: (item: PaletteItem) => void;
};

/**
 * „Schnell springen" — the palette's short-query view. Presentational: the
 * palette owns the highlight, because it spans the result groups too.
 *
 * Rows are `option`s, not buttons, and stay out of the tab order: focus never
 * leaves the search field, and the highlight travels via
 * `aria-activedescendant` instead. That is what keeps ↑/↓, Enter and a click
 * agreeing on which row is meant — a tabbable row would let focus and the
 * highlight point at two different destinations.
 */
export function QuickNav({ listboxId, items, activeIndex, onHighlight, onSelect }: Props) {
  const t = useTranslations('pages.shell.search');

  return (
    <div className="p-2">
      <p className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {t('quick-nav-title')}
      </p>
      <ul id={listboxId} role="listbox" aria-label={t('quick-nav-title')} className="flex flex-col">
        {items.map((item, index) => {
          const Icon = ENTRY_ICON[item.key] ?? ContactsIcon;

          return (
            <li
              key={item.key}
              id={item.optionId}
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => onHighlight(index)}
              onClick={() => onSelect(item)}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-colors',
                index === activeIndex ? 'bg-selected-bg' : 'hover:bg-hover-bg',
              )}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-neutral-100">
                <Icon className="h-3.5 w-3.5 text-text-secondary" aria-hidden />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{item.primary}</span>
              {index === activeIndex && <Kbd>{'\u21b5'}</Kbd>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
